// Sheet 1 of the Excel report, "All data": the customer's template (Documentation/excel-report/Week_43_Tomtabel.xlsx,
// sheet dbo_TIS_AlleDataExport_fsp) - one row per trial, 55 columns A-BC with the template's headers and order. The
// differences are decided ones: "PO" is "CO" (contracts.co), "Project" is "Protocol", "PlotSizeB" is "PlotSizeW", a
// done task is a real date and a planned / Time interval one text (taskCells.js), and the report's look
// (cellStyle.js): headers by category, task cells by status, the CO groups, A-D frozen. No next-task frame here: the
// next task is the work sheets' (the design).
// The data is a trial row from POST /v1/trials/export-rows.
import { gpsFieldsByAxis } from '../gps';
import { addAllDataColumns } from './allDataColumns';
import { ACTIVITY_VANOS, APPLICATION_LETTERS, VISIT_NUMBERS, taskCell, tasksByColumn } from './taskCells';
import { cellStatus } from './cellStatus';
import {
  addHeaderRow, categoryStarts, coGroups, earthSampleStatus, fallbackFill, isTaskColumn, scriptRedRule, setDataCell,
  setUpSheet, styleCell,
} from './cellStyle';

export { DATE_FORMAT, cleanCellValue } from './cellStyle';
// The sheet's name in the file (the customer's choice, 30-10-2026; Work_Sorting.py takes the active sheet, not a
// name), and in the template
export const BASE_SHEET_NAME = 'All data';
export const TEMPLATE_SHEET_NAME = 'dbo_TIS_AlleDataExport_fsp';

// A task column: its cell value is the task's (taskCells.js), and it knows its task for the task's status
const taskColumn = (header, taskOf) => [header, (t) => taskCell(taskOf(t)), taskOf];
const activity = (header, vano) => taskColumn(header, (t) => t.byColumn.activities[vano]);

// [header, value, the task (task columns)]
const COLUMNS = [
  ['CO', (t) => t.co],
  ['CustomerNo', (t) => t.customer_no],
  ['Protocol', (t) => t.protocol],
  ['Trial', (t) => t.trialno],
  ['PI', (t) => t.pi],
  ['FT', (t) => t.ft],
  ['LocatIonTarget', (t) => t.location_target],
  ['Trialaddress', (t) => t.trial_address],
  activity('Reserved area', ACTIVITY_VANOS.reservedArea),
  activity('Stacked out', ACTIVITY_VANOS.stackedOut),
  activity('Borders sprayed', ACTIVITY_VANOS.bordersSprayed),
  ['TreatmentCount', (t) => t.treatment_count],
  ['ReplicationCount', (t) => t.replication_count],
  ['EarthSample', (t) => t.earth_sample],
  activity('Plot size', ACTIVITY_VANOS.plotSize),
  ['PlotSizeL', (t) => t.plot_size_l],
  // The template's "PlotSizeB": the plot's width (W, as the app calls it)
  ['PlotSizeW', (t) => t.plot_size_b],
  ['Country', (t) => t.country],
  ['Area', (t) => t.area],
  ['Crop', (t) => t.crop],
  ['Host', (t) => t.host],
  ['Type', (t) => t.type],
  ['SD', (t) => t.sd],
  ...APPLICATION_LETTERS.map((letter) => taskColumn(letter, (t) => t.byColumn.applications[letter])),
  ...VISIT_NUMBERS.map((visit) => taskColumn(visit, (t) => t.byColumn.visits[visit])),
  // Harvest and Destruction are the trial's flags, TRUE / FALSE as in Week 43 (Harvest "to be decided" counts as TRUE)
  ['Harvest', (t) => t.harvest === 1 || t.harvest === 2],
  activity('Pegs up', ACTIVITY_VANOS.pegsUp),
  ['Destruction', (t) => Boolean(t.destruction)],
  ['PosGPSLongitude', (t) => t.gps.longitude],
  ['PosGPSLatitude', (t) => t.gps.latitude],
];

export const BASE_HEADERS = COLUMNS.map(([header]) => header);

/** The 0-based index of a column of sheet 1 by its header (the work sheets and Work_Sorting.py use these). */
export const COL = Object.fromEntries(BASE_HEADERS.map((header, i) => [header, i]));

const blankToNull = (value) => (value === undefined || value === '' ? null : value);

/**
 * A trial row's 55 values (null for an empty cell) and their statuses on `exportDate` (cellStatus for a task
 * column, the EarthSample's, else null), plus what the report says about it: which of the activities it has a task
 * for (an empty cell does not tell), whether its GPS fields were the other way round, how many of its visits /
 * applications have no column (V15+, M-Z, ...), and how many tasks lost their column to another with the same number;
 * and its tasks by column (tasksByColumn), for the work sheets' next task.
 */
export function baseRow(trial, exportDate = new Date()) {
  const byColumn = tasksByColumn(trial.tasks ?? []);
  const gps = gpsFieldsByAxis(trial.gps_latitude_field, trial.gps_longitude_field);
  const t = { ...trial, byColumn, gps };
  const values = COLUMNS.map(([, value]) => blankToNull(value(t)));
  return {
    values,
    statuses: COLUMNS.map(([header, , taskOf], c) => {
      if (taskOf) return taskOf(t) ? cellStatus(taskOf(t), exportDate) : null;
      return header === 'EarthSample' ? earthSampleStatus(values[c]) : null;
    }),
    activities: Object.keys(byColumn.activities),
    byColumn,
    gpsSwapped: gps.swapped,
    notShown: byColumn.notShown,
    duplicates: byColumn.duplicates,
  };
}

/**
 * Adds sheet 1 for the trial rows (in this order) on `exportDate` to an ExcelJS workbook: the template's 55 columns,
 * then all the other data (allDataColumns.js); returns the rows (baseRow).
 */
export function addBaseSheet(workbook, trials, exportDate = new Date()) {
  const sheet = workbook.addWorksheet(BASE_SHEET_NAME);
  addHeaderRow(sheet, BASE_HEADERS);
  setUpSheet(sheet, COL.Trial + 1); // A-D

  const rows = trials.map((trial) => baseRow(trial, exportDate));
  const starts = categoryStarts(BASE_HEADERS);
  const groups = coGroups(rows.map(({ values }) => values[COL.CO]));
  rows.forEach(({ values, statuses }, r) => {
    const row = sheet.getRow(r + 2);
    const warnings = scriptRedRule(BASE_HEADERS, values, statuses);
    const { shade, coStart } = groups[r];
    BASE_HEADERS.forEach((header, c) => {
      const cell = row.getCell(c + 1);
      setDataCell(cell, values[c], { categoryStart: starts[c], coStart, task: isTaskColumn(header) });
      styleCell(cell, { status: statuses[c]?.status, warning: warnings.includes(c), fallbackFill: fallbackFill(header, shade) });
    });
  });
  // Then all the other data, after the template's columns (customer point 66)
  const added = addAllDataColumns(sheet, trials, COLUMNS.length + 1);
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: Math.max(1, rows.length + 1), column: COLUMNS.length + added } };
  return rows;
}
